import { PropsWithStyles, StyledProps } from '@front10';
import { Context } from '@front10/helpers/dist/Context';
import useStyles from '@front10/helpers/dist/UseStyles';
import React, { useContext } from 'react';
import Box from '../../elements/Box';
import FareItemCallToAction from '../../items/_commons/atoms/FareItemCallToAction';
import FareItemDatesInfo, {
  FareItemDatesInfoProps
} from '../../items/_commons/atoms/FareItemDatesInfo';
import FareItemJourneyType, {
  FareItemJourneyTypeProps
} from '../../items/_commons/atoms/FareItemJourneyType';
import FareItemLastSeen from '../../items/_commons/atoms/FareItemLastSeen';
import FareItemOriginDestination from '../../items/_commons/atoms/FareItemOriginDestination';
import FareItemPrice from '../../items/_commons/atoms/FareItemPrice';
import FareItemMiles from '../../items/_commons/atoms/FareItemMiles';
import FareItemTripDuration from '../../items/_commons/atoms/FareItemTripDuration';
import { handleFareItemInteraction } from '../../items/_commons/commonFns';
import { FareTableField } from './FareTable';
import FareItemTravelClass, {
  FareItemTravelClassProps
} from '../../items/_commons/atoms/FareItemTravelClass';
import Separator from '../../items/_commons/atoms/Separator';
import { BoxProps } from '../../elements/Box/Box';

interface Props {
  callToActionOnClick: (props: { data: FareItem }, label: string) => void;
  fareItem: $TSFixMe;
  fareItemCallToAction: $TSFixMe;
  fareItemTripInfo: StyledProps;
  fareItemDates?: Partial<FareItemDatesInfoProps>;
  fareItemJourneyType?: Exclude<FareItemJourneyTypeProps, 'journeyType'>;
  fareItemTravelClass?: Exclude<FareItemTravelClassProps, 'farenetTravelClass'>;
  fareItemTripType?: Pick<
    CustomTripTypeProps,
    'showJourneyType' | 'showTravelClass' | 'separator' | 'container'
  >;
  fareItemOrigin?: React.ComponentPropsWithoutRef<typeof FareItemOriginDestination>;
  fareItemDestination?: React.ComponentPropsWithoutRef<typeof FareItemOriginDestination>;
  cellSettings?: StyledProps;
  fields: FareTableField[];
  interactionHotZone: FareItemInteractionHotZone;
  onClick?: $TSFixMe;
  onKeyDown?: $TSFixMe;
}

interface CustomTripInfoProps {
  data: FareItem;
  showTripDuration?: boolean;
  showPrice?: boolean;
  showMiles?: boolean;
  showLastSeen: boolean;
  lastSeenPosition: 'before-price' | 'after-price';
  fareItemPrice: StyledProps;
  fareItemMiles: StyledProps;
  fareItemLastSeen: StyledProps;
  fareItemTripDuration: StyledProps;
}

function FareItemTripInfo({
  data,
  showTripDuration = false,
  showPrice = true,
  showMiles = false,
  showLastSeen = true,
  lastSeenPosition = 'after-price',
  fareItemPrice,
  fareItemMiles,
  fareItemLastSeen,
  fareItemTripDuration,
  ...props
}: StyledProps<CustomTripInfoProps>) {
  const fareItemLastSeenAtom = showLastSeen && data && (
    <FareItemLastSeen {...fareItemLastSeen} {...data} />
  );

  return (
    <Box {...props}>
      {/* FareItemTripDuration */}
      {showTripDuration && <FareItemTripDuration {...fareItemTripDuration} {...data} />}
      {lastSeenPosition === 'before-price' && fareItemLastSeenAtom}
      {/* FareItemPrice */}
      {showPrice && <FareItemPrice {...fareItemPrice} {...data} />}
      {showMiles && <FareItemMiles {...fareItemMiles} {...data} />}
      {lastSeenPosition === 'after-price' && fareItemLastSeenAtom}
    </Box>
  );
}

interface CustomTripTypeProps {
  data: FareItem;
  showJourneyType?: boolean;
  showTravelClass?: boolean;
  fareItemJourneyType: StyledProps;
  fareItemTravelClass: StyledProps;
  container?: BoxProps;
  separator?: React.ComponentPropsWithoutRef<typeof Separator>;
}

function FareItemTripType({
  data,
  showJourneyType = true,
  showTravelClass = false,
  fareItemJourneyType,
  fareItemTravelClass,
  container,
  separator = {
    content: '/'
  }
}: CustomTripTypeProps) {
  return (
    <Box display="flex" rtlReverse {...container}>
      {showJourneyType && (
        <FareItemJourneyType journeyType={data.journeyType} {...fareItemJourneyType} />
      )}
      {showTravelClass && showJourneyType && <Separator {...separator} />}
      {showTravelClass && (
        <FareItemTravelClass
          farenetTravelClass={data.farenetTravelClass}
          {...fareItemTravelClass}
        />
      )}
    </Box>
  );
}

const components: Record<FareTableField, $TSFixMe> = {
  origin: FareItemOriginDestination,
  destination: FareItemOriginDestination,
  dates: FareItemDatesInfo,
  'fare-type': FareItemTripType,
  price: FareItemTripInfo,
  book: FareItemCallToAction
} as const;

function FareItemRow(
  {
    styles = {},
    fareItem,
    fareItemCallToAction = { showCallToActionTextMin: false },
    fareItemTripInfo,
    fareItemDates,
    fareItemOrigin,
    fareItemDestination,
    fareItemPrice,
    fareItemMiles,
    fareItemLastSeen,
    fareItemTripDuration,
    fareItemTravelClass,
    fareItemTripType = {},
    showTripDuration,
    showPrice,
    showMiles,
    showLastSeen,
    lastSeenPosition,
    fields,
    callToActionOnClick,
    interactionHotZone = 'smart',
    onClick,
    onKeyDown,
    fareItemJourneyType,
    cellSettings = {}
  }: PropsWithStyles<FareItem & Props & Exclude<CustomTripInfoProps, 'data'>>,
  ref: React.Ref<HTMLTableRowElement>
) {
  const { theme, direction } = useContext(Context);
  const [className, style] = useStyles(styles, { theme, direction });
  const [cellClass, cellStyle] = useStyles(cellSettings, { direction, theme });
  const showCallToAction = fields.some((value) => value === 'book');

  const {
    handleCallToAction,
    handleClick,
    handleItemKeyDown,
    handleCallToActionKeyDown,
    hasInteraction
  } = handleFareItemInteraction({
    data: fareItem,
    callToActionOnClick,
    interactionHotZone,
    showCallToAction,
    onClick,
    onKeyDown
  });

  const cursorClass = hasInteraction ? 'fr-cursor-pointer' : '';

  const fieldProps = {
    origin: {
      showDestination: false,
      showOriginAirportCode: true,
      ...fareItem,
      ...fareItemOrigin
    },
    destination: {
      showOrigin: false,
      showDestinationAirportCode: true,
      ...fareItem,
      ...fareItemDestination
    },
    dates: {
      layout: 'column',
      data: fareItem,
      ...fareItemDates
    },
    'fare-type': {
      data: fareItem,
      showTravelClass: fareItemTripType.showTravelClass,
      showJourneyType: fareItemTripType.showJourneyType,
      separator: fareItemTripType.separator,
      fareItemJourneyType,
      fareItemTravelClass,
      container: fareItemTripType.container
    },
    price: {
      data: fareItem,
      showTripDuration,
      showPrice,
      showMiles,
      showLastSeen,
      lastSeenPosition,
      fareItemPrice,
      fareItemMiles,
      fareItemLastSeen,
      fareItemTripDuration,
      ...fareItemTripInfo
    },
    book: {
      ...fareItemCallToAction,
      ...fareItem,
      onClick: handleCallToAction,
      onKeyDown: handleCallToActionKeyDown
    }
  } as const;

  return (
    <tr
      ref={ref}
      onClick={handleClick}
      onKeyDown={handleItemKeyDown}
      className={`${className} fr-focus ${cursorClass}`}
      style={style}
      tabIndex={0}
    >
      {fields.map((item) => {
        const Component = components[item];
        return (
          <td key={item} className={cellClass} style={cellStyle}>
            <Component {...fieldProps[item]} />
          </td>
        );
      })}
    </tr>
  );
}

export default React.forwardRef(FareItemRow);
